import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Logo from "../../molecules/logo/Logo";
import { signUp } from "src/components/organisms/auth";
import InputBox from "src/components/atoms/v2/inputBox/inputBox";
import ButtonPro from "src/components/atoms/primary-button/primary-button";
import LeftSection from "./left-section";
import LoadingModal from "src/components/molecules/loading/LoadingModal";
import { ErrorDisplay } from "src/components/atoms/error/error";
import HyperLink from "src/components/atoms/hyper-link/hyper-link";
import CheckBox from "src/components/molecules/check-box/check-box";
import PhoneNumberField from "src/components/molecules/phoneNumberField/PhoneNumberField";
import { IPhoneNumber } from "src/types/models/apiModels";

const SignUp: React.FC = () => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [termsAgreed, setTermsAgreed] = useState(false);
	const [firstName, setFirstName] = useState("");
	const [familyName, setFamilyName] = useState("");
	const [businessEmail, setBusinessEmail] = useState("");
	const [mobileNumber, setMobileNumber] = useState("");
	const [iMobileNumber, setIMobileNumber] = useState<IPhoneNumber>({
		countryCode: "",
		number: "",
	});
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");

	const [fieldValidity, setFieldValidity] = useState({
		firstName: false,
		lastName: false,
		email: false,
		phone: false,
		password: false,
		confirmPassword: false,
	});

	const [passwordMismatch, setPasswordMismatch] = useState(false);

	const updateValidity = (
		field: keyof typeof fieldValidity,
		isValid: boolean
	) => {
		setFieldValidity((prev) => ({ ...prev, [field]: isValid }));
	};

	useEffect(() => {
		const mismatch =
			password !== "" &&
			confirmPassword !== "" &&
			password !== confirmPassword;
		setPasswordMismatch(mismatch);
		setError(mismatch ? "Password and Confirm Password do not match" : "");
	}, [password, confirmPassword]);

	const allFieldsValid = Object.values(fieldValidity).every((val) => val);

	const handleSignUp = (event: React.FormEvent) => {
		event.preventDefault();
		setError("");

		if (password !== confirmPassword) {
			setError("Password and confirm password do not match");
			return;
		}

		setLoading(true);

		signUp(
			businessEmail,
			password,
			{
				given_name: firstName,
				family_name: familyName,
				phone_number: mobileNumber,
			},
			(err, result) => {
				setLoading(false);
				if (err) {
					setError(err.message || JSON.stringify(err));
					return;
				}
				navigate("/verify", { state: { businessEmail, password } });
			}
		);
	};

	return (
		<div className="md:bg-app-gradient flex items-start justify-center md:items-center md:justify-center h-screen">
			<div className="flex">
				<LeftSection />
				<div className="bg-transparent md:bg-white md:flex md:flex-col md:items-center md:justify-evenly w-screen max-w-[550px] px-12 py-12 rounded-r-2xl md:shadow-[0_30px_50px_rgba(0,0,0,.5)]">
					<div className="flex flex-col items-center gap-4 md:gap-2">
						<div className="md:hidden py-4">
							<Logo />
						</div>
						<span className="font-Poppins font-semibold tracking-wider bg-gradient-to-bl from-purple-600 to-red-600 text-4xl md:text-6xl lg:text-4xl bg-clip-text text-transparent uppercase drop-shadow-2xl">
							CREATE ACCOUNT
						</span>
						<label className="font-Poppins text-sm font-semibold text-gray-900 uppercase">
							Have an account?{" "}
							<HyperLink href="/login" label="Sign-in here." />
						</label>
					</div>

					<form
						className="flex flex-col items-center gap-4 w-full"
						onSubmit={handleSignUp}
					>
						<div className="flex flex-col items-center gap-2 w-full py-4">
							<InputBox
								value={firstName}
								onChange={(value, isValid) => {
									setFirstName(value);
									if (isValid !== undefined)
										updateValidity("firstName", isValid);
								}}
								label="First name"
								type="text"
								required
								allowedPattern={/^[a-zA-ZÀ-ÿ'\- ]{1,100}$/}
								maxLength={32}
							/>
							<InputBox
								value={familyName}
								onChange={(value, isValid) => {
									setFamilyName(value);
									if (isValid !== undefined)
										updateValidity("lastName", isValid);
								}}
								label="Last name"
								type="text"
								required
								allowedPattern={/^[a-zA-ZÀ-ÿ'\- ]{1,100}$/}
								maxLength={32}
							/>
							{/* <InputBox
								value={mobileNumber}
								onChange={(value, isValid) => {
									setMobileNumber(value);
									if (isValid !== undefined)
										updateValidity("phone", isValid);
								}}
								label="Phone number"
								type="tel"
								required
								maxLength={12}
							/> */}
							<PhoneNumberField
								label="Phone number"
								phoneNumber={iMobileNumber}
								value={mobileNumber}
								onChange={(phoneNumber, value) =>
									setMobileNumber(value)
								}
								showCountry={false}
								required
							></PhoneNumberField>
							<InputBox
								value={businessEmail}
								onChange={(value, isValid) => {
									setBusinessEmail(value);
									if (isValid !== undefined)
										updateValidity("email", isValid);
								}}
								label="Business email"
								type="email"
								required
								maxLength={254}
							/>
							<InputBox
								value={password}
								onChange={(value, isValid) => {
									setPassword(value);
									if (isValid !== undefined)
										updateValidity("password", isValid);
								}}
								label="Password"
								type="password"
								required
								maxLength={32}
							/>
							<InputBox
								value={confirmPassword}
								onChange={(value, isValid) => {
									setConfirmPassword(value);
									if (isValid !== undefined)
										updateValidity(
											"confirmPassword",
											isValid
										);
								}}
								label="Confirm password"
								type="password"
								required
								maxLength={32}
							/>
						</div>
						<div className="flex items-center text-left w-full">
							<CheckBox
								id="chk-terms"
								onChange={(value) => setTermsAgreed(value)}
								// disabled
							/>
							<label className="font-Poppins ms-2 text-sm font-semibold text-gray-900">
								I agree with the{" "}
								<HyperLink
									target="blank"
									href="https://securedcalls.com/terms"
									label="terms and conditions."
								/>
							</label>
						</div>
						<ErrorDisplay error={error} />
						<div className="pt-12 md:pt-2 pb-4 w-full">
							<ButtonPro
								label="Submit"
								onClick={() => handleSignUp}
								disabled={
									!allFieldsValid ||
									!termsAgreed ||
									!firstName ||
									!familyName ||
									!mobileNumber ||
									!businessEmail ||
									!password ||
									!confirmPassword
								}
							/>
						</div>
					</form>
				</div>
			</div>
			<LoadingModal open={loading} message="Please wait..." />
		</div>
	);
};

export default SignUp;
